import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Collapse} from '@mui/material';
import Hls from 'hls.js';
import PropTypes from 'prop-types';
import {getScreens} from 'API/v1/GetScreens';
import {stopPlayer} from 'API/v1/StopPlayer';
import PlayerChrome from 'components/channels/PlayerChrome';
import ScreenManagerPanel from 'components/channels/ScreenManagerPanel';
import UltraPlayerStatsPanel from 'components/channels/UltraPlayerStatsPanel';
import useAudioTracks from 'hooks/useAudioTracks';
import {appendVid} from 'utils/antiCutEngine';
import {createHlsAudioLoader} from 'utils/audioTrackLoaders';
import {
  SCREENS_POLL_MS,
  dHlsErrorAction,
  dStallDecision,
  dStartupDecision,
  relayAliveness,
  resumeSeekTarget
} from 'utils/dPlayerEngine';
import {resolveDPlaySession, D_SESSION_BOOTSTRAP_TIMEOUT_MS} from 'utils/dPlayerSession';
import {playerHealth} from 'utils/playerHealth';
import {classifyPlaybackFailure, PLAYER_STATES} from 'utils/playerPlayback';
import {
  HEALTHY_RESET_MS,
  RECOVERY_BUFFER_SECONDS,
  RESTART_COALESCE_MS,
  TARGET_BUFFER_SECONDS,
  bufferedSecondsAhead,
  computeUltraBackoffDelay,
  evaluatePlayheadProgress,
  nextUltraAttempt,
  resumePositionForSequence
} from 'utils/ultraPlayerEngine';
import {
  appendUltraHistory,
  formatSeconds,
  relaySampleIsFresh,
  relayStatusBackoffMs,
  reservePresentation,
  reserveTrendOverFiveSeconds,
  strictBufferedSecondsAhead,
  validateRelayStatusPayload
} from 'utils/ultraPlayerStats';

/**
 * Player D — freeze-profile web client.
 *
 * Fork of UltraPlayer.js (the reference implementation of the d7dc7ce
 * anti-loop invariants) with the C-specific recovery replaced by:
 *  - infinite freeze patience while the relay says the session is alive,
 *  - the screen manager instead of the limit.ts slate,
 *  - no mpegts compatibility path and no legacy raw-media fallback.
 * Every d7dc7ce invariant carried over is marked with its original comment.
 */

const INITIAL_CONNECT_DELAY_MS = 800;
const MONITOR_INTERVAL_MS = 500;
const RECOVERED_FLASH_MS = 2500;
const STATUS_POLL_INTERVAL_MS = 1000;
const STATS_SAMPLE_INTERVAL_MS = 1000;
const STATUS_FETCH_TIMEOUT_MS = 5000;
/** No playhead progress for this long, with the relay alive, is a freeze. */
const FREEZE_DETECT_MS = 2000;

function browserIsOnline() {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

function finiteMetric(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function droppedVideoFrames(video) {
  try {
    const quality = video?.getVideoPlaybackQuality?.();
    const value = quality?.droppedVideoFrames ?? video?.webkitDroppedFrameCount;
    return finiteMetric(value);
  } catch (_) {
    return null;
  }
}

function readHlsDiagnostics(hls, video, bandwidthMeasured) {
  if (!hls) {
    return {
      edgeDistanceSeconds: null,
      hlsTargetSeconds: null,
      declaredBitrate: null,
      bandwidthEstimate: null,
      levelLabel: null
    };
  }
  try {
    const levelIndex = [hls.currentLevel, hls.loadLevel, hls.nextLoadLevel].find(
      (value) => Number.isInteger(value) && value >= 0
    );
    const level = Number.isInteger(levelIndex) ? hls.levels?.[levelIndex] : null;
    const hasLiveDetails = Boolean(hls.latestLevelDetails);
    return {
      edgeDistanceSeconds: hasLiveDetails ? finiteMetric(hls.latency) : null,
      hlsTargetSeconds: hasLiveDetails ? finiteMetric(hls.targetLatency) : null,
      declaredBitrate: finiteMetric(level?.bitrate),
      bandwidthEstimate: bandwidthMeasured ? finiteMetric(hls.bandwidthEstimate) : null,
      levelLabel: level?.name || (Number.isInteger(levelIndex) ? `Nivel ${levelIndex + 1}` : null),
      width: finiteMetric(video?.videoWidth || level?.width),
      height: finiteMetric(video?.videoHeight || level?.height)
    };
  } catch (_) {
    return {
      edgeDistanceSeconds: null,
      hlsTargetSeconds: null,
      declaredBitrate: null,
      bandwidthEstimate: null,
      levelLabel: null,
      width: finiteMetric(video?.videoWidth),
      height: finiteMetric(video?.videoHeight)
    };
  }
}

const EMPTY_SCREENS = Object.freeze({
  screens: [],
  maxActiveStreams: null,
  activeCount: null,
  loading: false,
  error: false,
  kickingKeys: []
});

function DPlayer({
  playURL,
  playingName,
  viewId,
  bindVideoElement,
  userPaused,
  onUserPausedChange,
  onRefreshVariant,
  advancedStatsEnabled = false,
  socket = null
}) {
  const [playback, setPlayback] = useState({
    status: PLAYER_STATES.PREPARING,
    attempt: 0,
    retryAt: null,
    reason: null
  });
  const [bufferSeconds, setBufferSeconds] = useState(null);
  const [online, setOnline] = useState(browserIsOnline);
  const [relayStatusUrl, setRelayStatusUrl] = useState(null);
  const [relaySample, setRelaySample] = useState(null);
  const [history, setHistory] = useState([]);
  const [statsNow, setStatsNow] = useState(() => Date.now());
  const [screenState, setScreenState] = useState(EMPTY_SCREENS);
  const [ownServerViewId, setOwnServerViewId] = useState(null);
  const [slotStarting, setSlotStarting] = useState(false);
  const [diagnostics, setDiagnostics] = useState({
    localBufferSeconds: null,
    reserveTrend: null,
    relayState: null,
    playerState: PLAYER_STATES.PREPARING,
    edgeDistanceSeconds: null,
    hlsTargetSeconds: null,
    transport: null,
    width: null,
    height: null,
    declaredBitrate: null,
    bandwidthEstimate: null,
    levelLabel: null,
    droppedFrames: null,
    online: browserIsOnline(),
    retryAttempt: 0
  });

  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const {audio, audioTracks, selectedAudioTrack} = useAudioTracks(playURL, videoRef);
  const hlsRef = useRef(null);
  const playbackRef = useRef(playback);
  const initialTimerRef = useRef(null);
  const restartTimerRef = useRef(null);
  const recoveredTimerRef = useRef(null);
  const monitorTimerRef = useRef(null);
  const statsTimerRef = useRef(null);
  const sessionAbortRef = useRef(null);
  const sessionTimeoutRef = useRef(null);
  const sessionRequestGenerationRef = useRef(0);
  const attemptRef = useRef(0);
  const healthySinceRef = useRef(null);
  const createdAtRef = useRef(0);
  const lastProgressAtRef = useRef(0);
  const lastCurrentTimeRef = useRef(-1);
  const readyToPlayRef = useRef(false);
  const playRequestedRef = useRef(false);
  const userPausedRef = useRef(userPaused);
  const autoplayBlockedRef = useRef(false);
  const deferredFailureRef = useRef(null);
  const needsRecoveryConfirmationRef = useRef(false);
  const onlineRef = useRef(browserIsOnline());
  const bandwidthMeasuredRef = useRef(false);
  const manifestParsedRef = useRef(false);
  const mediaRecoveryAttemptsRef = useRef(0);
  const inplaceReloadAttemptsRef = useRef(0);
  const lastMediaRecoveryAtRef = useRef(0);
  const lastPlaylistReloadAtRef = useRef(0);
  const lastCcRef = useRef(-1);
  const lastFragRef = useRef(null);
  const resumeRef = useRef(null);
  const activePlaylistUrlRef = useRef(null);
  const engineGenerationRef = useRef(0);
  const sessionGenerationRef = useRef(0);
  const transportRef = useRef(null);
  const relaySampleRef = useRef(null);
  const relayStatusUrlRef = useRef(null);
  const historyRef = useRef([]);
  const dSessionRef = useRef(null);
  const waitingSinceRef = useRef(0);
  const frozenReportedRef = useRef(false);
  const frozenChipReportedRef = useRef(false);
  const frozenSinceRef = useRef(0);
  const alivenessStatusRef = useRef('alive');
  const createEngineRef = useRef(() => {});
  const scheduleRestartRef = useRef(() => {});
  const restartNowRef = useRef(() => {});
  const beginPlaybackRef = useRef(() => {});
  const refreshScreensRef = useRef(() => {});
  const startFreedTransportRef = useRef(() => {});

  useEffect(() => {
    playbackRef.current = playback;
  }, [playback]);

  useEffect(() => {
    relaySampleRef.current = relaySample;
  }, [relaySample]);

  useEffect(() => {
    relayStatusUrlRef.current = relayStatusUrl;
  }, [relayStatusUrl]);

  const reserveView = useMemo(
    () => reservePresentation(relaySample, history, statsNow),
    [history, relaySample, statsNow]
  );
  const reserveChipLabel = `Colchón D ${formatSeconds(reserveView.reserveSeconds)}`;

  useEffect(() => {
    userPausedRef.current = userPaused;
    if (
      userPaused &&
      playbackRef.current.status !== PLAYER_STATES.OFFLINE &&
      playbackRef.current.status !== PLAYER_STATES.TERMINAL_ERROR &&
      playbackRef.current.status !== PLAYER_STATES.WAITING_SLOT
    ) {
      const next = {...playbackRef.current, status: PLAYER_STATES.PAUSED, reason: null};
      playbackRef.current = next;
      setPlayback(next);
    }
  }, [userPaused]);

  const updatePlayback = useCallback((patch) => {
    const previous = playbackRef.current;
    const next = {...previous, ...patch};
    if (patch.status && patch.status !== PLAYER_STATES.RECOVERED && recoveredTimerRef.current) {
      window.clearTimeout(recoveredTimerRef.current);
      recoveredTimerRef.current = null;
    }
    playbackRef.current = next;
    setPlayback(next);
    if (next.status !== previous.status) {
      playerHealth.report('player.stateChanged', {
        from: previous.status,
        to: next.status,
        variant: 'D',
        reason: next.reason || null
      });
    }
  }, []);

  const clearRestartTimer = useCallback(() => {
    if (restartTimerRef.current) window.clearTimeout(restartTimerRef.current);
    restartTimerRef.current = null;
  }, []);

  const resetFreezeTracking = useCallback(() => {
    frozenReportedRef.current = false;
    frozenChipReportedRef.current = false;
    frozenSinceRef.current = 0;
  }, []);

  const destroyEngine = useCallback(() => {
    engineGenerationRef.current += 1;
    const hls = hlsRef.current;
    hlsRef.current = null;
    transportRef.current = null;
    bandwidthMeasuredRef.current = false;
    if (hls) {
      try {
        hls.destroy();
      } catch (_) {
        // The next generation owns the media element now.
      }
    }
    const video = videoRef.current;
    if (video) {
      try {
        video.pause();
        video.removeAttribute('src');
        video.load();
      } catch (_) {
        // Native HLS may already have detached the element.
      }
    }
  }, []);

  const deferFailureWhilePaused = useCallback(
    (failure) => {
      clearRestartTimer();
      deferredFailureRef.current = {
        retryable: failure?.retryable !== false,
        reason: failure?.reason || 'network',
        statusCode: failure?.statusCode ?? null
      };
      destroyEngine();
      updatePlayback({status: PLAYER_STATES.PAUSED, retryAt: null, reason: null});
    },
    [clearRestartTimer, destroyEngine, updatePlayback]
  );

  const showRecovered = useCallback(() => {
    if (recoveredTimerRef.current) window.clearTimeout(recoveredTimerRef.current);
    const sessionGeneration = sessionGenerationRef.current;
    needsRecoveryConfirmationRef.current = false;
    updatePlayback({status: PLAYER_STATES.RECOVERED, retryAt: null, reason: null});
    playerHealth.report('player.recovered', {variant: 'D', attempt: attemptRef.current});
    playerHealth.report('d.recovered', {
      attempt: attemptRef.current,
      bufferSeconds: Number(
        bufferedSecondsAhead(videoRef.current?.buffered, videoRef.current?.currentTime).toFixed(2)
      )
    });
    Promise.resolve(onRefreshVariant?.('recovered')).catch(() => {});
    recoveredTimerRef.current = window.setTimeout(() => {
      recoveredTimerRef.current = null;
      if (
        sessionGeneration !== sessionGenerationRef.current ||
        playbackRef.current.status !== PLAYER_STATES.RECOVERED
      )
        return;
      if (!onlineRef.current) updatePlayback({status: PLAYER_STATES.OFFLINE, reason: 'offline'});
      else if (userPausedRef.current || autoplayBlockedRef.current) {
        updatePlayback({
          status: PLAYER_STATES.PAUSED,
          reason: autoplayBlockedRef.current ? 'autoplayBlocked' : null
        });
      } else updatePlayback({status: PLAYER_STATES.PLAYING, reason: null});
    }, RECOVERED_FLASH_MS);
  }, [onRefreshVariant, updatePlayback]);

  const handleAutoplayBlocked = useCallback(() => {
    if (userPausedRef.current || playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR)
      return;
    playRequestedRef.current = false;
    autoplayBlockedRef.current = true;
    updatePlayback({status: PLAYER_STATES.PAUSED, retryAt: null, reason: 'autoplayBlocked'});
    playerHealth.report('player.autoplayBlocked', {variant: 'D'});
    playerHealth.report('d.autoplayBlocked', {});
  }, [updatePlayback]);

  const beginPlayback = useCallback(
    (mode = 'resume', available = 0) => {
      const video = videoRef.current;
      if (!video || !readyToPlayRef.current) return;
      if (userPausedRef.current) {
        updatePlayback({status: PLAYER_STATES.PAUSED, reason: null});
        return;
      }
      autoplayBlockedRef.current = false;
      playRequestedRef.current = true;
      const generation = engineGenerationRef.current;
      const sessionGeneration = sessionGenerationRef.current;
      const requestedVideo = video;
      playerHealth.report('d.bufferReady', {
        mode,
        bufferSeconds: Number(available.toFixed(2)),
        targetSeconds: TARGET_BUFFER_SECONDS
      });
      const promise = video.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(() => {
          if (
            generation !== engineGenerationRef.current ||
            sessionGeneration !== sessionGenerationRef.current ||
            requestedVideo !== videoRef.current
          )
            return;
          handleAutoplayBlocked();
        });
      }
    },
    [handleAutoplayBlocked, updatePlayback]
  );
  beginPlaybackRef.current = beginPlayback;

  const makeTerminal = useCallback(
    (classification) => {
      clearRestartTimer();
      deferredFailureRef.current = null;
      updatePlayback({
        status: PLAYER_STATES.TERMINAL_ERROR,
        retryAt: null,
        reason: classification.reason
      });
      // Keep terminal sticky before detaching; destroy may flush late media
      // callbacks which must never arm automatic recovery again.
      destroyEngine();
      playerHealth.report('player.terminalError', {
        variant: 'D',
        reason: classification.reason,
        statusCode: classification.statusCode
      });
    },
    [clearRestartTimer, destroyEngine, updatePlayback]
  );

  const createTransport = useCallback(
    (url) => {
      if (!url || !onlineRef.current) {
        updatePlayback({status: PLAYER_STATES.OFFLINE, retryAt: null, reason: 'offline'});
        return;
      }
      clearRestartTimer();
      destroyEngine();
      const video = videoRef.current;
      if (!video) return;
      const generation = engineGenerationRef.current;
      const urlWithVid = appendVid(url, viewId);

      bindVideoElement?.(video);
      createdAtRef.current = Date.now();
      lastProgressAtRef.current = createdAtRef.current;
      lastCurrentTimeRef.current = -1;
      readyToPlayRef.current = false;
      playRequestedRef.current = false;
      autoplayBlockedRef.current = false;
      manifestParsedRef.current = false;
      mediaRecoveryAttemptsRef.current = 0;
      inplaceReloadAttemptsRef.current = 0;
      lastMediaRecoveryAtRef.current = 0;
      lastPlaylistReloadAtRef.current = 0;
      lastCcRef.current = -1;
      lastFragRef.current = null;
      resetFreezeTracking();
      setSlotStarting(false);
      // A restart of the same relay session resumes where playback was; a
      // different playlist (channel change, replaced session) starts live.
      const resume = resumeRef.current;
      resumeRef.current = null;
      const resumeAt = resume && resume.playlistUrl === url ? resume : null;
      activePlaylistUrlRef.current = url;
      bandwidthMeasuredRef.current = false;
      setBufferSeconds(null);
      updatePlayback({
        status: userPausedRef.current ? PLAYER_STATES.PAUSED : PLAYER_STATES.STABILIZING,
        retryAt: null,
        reason: null
      });
      playerHealth.report('d.stabilizing', {targetSeconds: TARGET_BUFFER_SECONDS});

      audio.nativeMode = false;
      if (Hls.isSupported()) {
        let hls;
        const isCurrent = () =>
          engineGenerationRef.current === generation && hlsRef.current === hls;
        hls = new Hls({
          fLoader: createHlsAudioLoader(Hls.DefaultConfig.loader, audio, isCurrent),
          progressive: false,
          lowLatencyMode: false,
          // Manual startLoad: the position is chosen once the playlist is
          // known, so a restart can resume at the previous media sequence
          // instead of re-entering ten seconds behind the edge.
          autoStartLoad: false,
          maxBufferLength: 12,
          maxMaxBufferLength: 18,
          maxBufferSize: 64 * 1024 * 1024,
          backBufferLength: 30,
          liveSyncDuration: TARGET_BUFFER_SECONDS,
          // A finite MediaSource duration on a live stream lets the element
          // fire `ended` whenever the buffer runs dry at the edge.
          liveDurationInfinity: true,
          // No liveMaxLatencyDuration: hls.js must never hard-seek the viewer
          // forward on its own; the relay window already bounds latency.
          maxBufferHole: 0.5,
          nudgeMaxRetry: 5,
          nudgeOffset: 0.2,
          startFragPrefetch: true,
          manifestLoadingTimeOut: 10000,
          fragLoadingTimeOut: 10000,
          levelLoadingTimeOut: 10000
        });
        hlsRef.current = hls;
        transportRef.current = 'HLS.js';
        hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
          if (!isCurrent()) return;
          manifestParsedRef.current = true;
          playerHealth.report('d.manifestParsed', {
            levels: Array.isArray(data?.levels) ? data.levels.length : null,
            live: data?.levels?.[0]?.details?.live ?? null
          });
          const details = data?.levels?.[0]?.details || hls.levels?.[0]?.details || null;
          const position = resumeAt ? resumePositionForSequence(details, resumeAt) : -1;
          if (position >= 0) {
            playerHealth.report('d.resumedAtSequence', {
              sn: resumeAt.sn,
              position: Number(position.toFixed(2))
            });
          }
          try {
            hls.startLoad(position);
          } catch (_) {
            // hls.js already reports load failures through its ERROR event.
          }
        });
        hls.on(Hls.Events.FRAG_LOADED, () => {
          if (isCurrent()) bandwidthMeasuredRef.current = true;
        });
        hls.on(Hls.Events.FRAG_CHANGED, (_event, data) => {
          if (!isCurrent()) return;
          const frag = data?.frag;
          if (!frag) return;
          lastFragRef.current = {sn: frag.sn, start: frag.start, duration: frag.duration};
          if (Number.isFinite(frag.cc) && frag.cc !== lastCcRef.current) {
            const previous = lastCcRef.current;
            lastCcRef.current = frag.cc;
            if (previous >= 0) playerHealth.report('d.discontinuity', {cc: frag.cc});
          }
        });
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!isCurrent()) return;
          if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
          const now = Date.now();
          const {action, classification} = dHlsErrorAction(data, {
            manifestParsed: manifestParsedRef.current,
            mediaRecoveryAttempts: mediaRecoveryAttemptsRef.current,
            inplaceReloadAttempts: inplaceReloadAttemptsRef.current,
            aliveness: relayAliveness(relaySampleRef.current, now),
            lastPlaylistReloadAtMs: lastPlaylistReloadAtRef.current,
            nowMs: now
          });
          playerHealth.report('d.hlsError', {
            fatal: Boolean(data?.fatal),
            type: data?.type || null,
            details: data?.details || null,
            statusCode: classification.statusCode,
            action
          });

          if (userPausedRef.current && data?.fatal) {
            deferFailureWhilePaused(classification);
            return;
          }

          switch (action) {
            case 'terminal':
              makeTerminal(classification);
              return;
            case 'buffering':
              if (!userPausedRef.current) {
                updatePlayback({status: PLAYER_STATES.BUFFERING, reason: 'playbackStall'});
              }
              return;
            case 'holdFrozen':
            case 'ignore':
            case 'ignoreThrottled':
              // The freeze machinery owns presentation while the relay lives.
              return;
            case 'recoverMedia':
              mediaRecoveryAttemptsRef.current += 1;
              lastMediaRecoveryAtRef.current = now;
              updatePlayback({status: PLAYER_STATES.BUFFERING, reason: 'transport'});
              try {
                // hls.js re-attaches and resumes at the same currentTime; the
                // source is not reloaded, so nothing already watched replays.
                hls.recoverMediaError();
                lastCurrentTimeRef.current = -1;
                return;
              } catch (_) {
                break;
              }
            case 'reloadInPlace':
              inplaceReloadAttemptsRef.current += 1;
              lastMediaRecoveryAtRef.current = now;
              playerHealth.report('d.reloadInPlace', {
                details: data?.details || null,
                attempt: inplaceReloadAttemptsRef.current,
                whileFrozen: playbackRef.current.status === PLAYER_STATES.FROZEN
              });
              try {
                hls.startLoad(-1);
                lastCurrentTimeRef.current = -1;
                return;
              } catch (_) {
                break;
              }
            case 'reloadPlaylist':
              // Uncapped while the relay is alive: the stale-playlist retries
              // of a long freeze must never consume the restart budget.
              lastPlaylistReloadAtRef.current = now;
              playerHealth.report('d.reloadPlaylist', {details: data?.details || null});
              try {
                hls.startLoad(-1);
                lastCurrentTimeRef.current = -1;
                return;
              } catch (_) {
                break;
              }
            case 'restartSession':
              scheduleRestartRef.current('sessionStopped');
              return;
            default:
              break;
          }
          scheduleRestartRef.current(classification.reason);
        });
        // loadSource before attachMedia: attaching (and re-attaching inside
        // recoverMediaError) must never reload the manifest.
        hls.loadSource(urlWithVid);
        hls.attachMedia(video);
        playerHealth.report('d.playerCreated', {
          transport: 'hls.js',
          attempt: attemptRef.current,
          generation
        });
        return;
      }

      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        audio.nativeMode = true;
        transportRef.current = 'HLS nativo';
        video.src = urlWithVid;
        video.load();
        playerHealth.report('d.nativeFallback', {transport: 'native-hls'});
        playerHealth.report('d.playerCreated', {
          transport: 'native-hls',
          attempt: attemptRef.current,
          generation
        });
        return;
      }

      playerHealth.report('d.hlsUnsupported', {});
      makeTerminal(classifyPlaybackFailure(null, 'hlsUnsupported'));
    },
    [
      audio,
      bindVideoElement,
      clearRestartTimer,
      destroyEngine,
      deferFailureWhilePaused,
      makeTerminal,
      resetFreezeTracking,
      updatePlayback,
      viewId
    ]
  );

  const enterScreenManager = useCallback(
    (session) => {
      waitingSinceRef.current = Date.now();
      setSlotStarting(false);
      const block = session.screens;
      setScreenState({
        screens: block?.screens || [],
        maxActiveStreams: block?.maxActiveStreams ?? null,
        activeCount: block?.activeCount ?? null,
        loading: !block,
        error: false,
        kickingKeys: []
      });
      playerHealth.report('d.screenLimitShown', {
        used: block?.activeCount ?? null,
        max: block?.maxActiveStreams ?? null
      });
      updatePlayback({status: PLAYER_STATES.WAITING_SLOT, retryAt: null, reason: 'screenLimit'});
    },
    [updatePlayback]
  );

  const createEngine = useCallback(
    (url) => {
      if (!url || !onlineRef.current) {
        updatePlayback({status: PLAYER_STATES.OFFLINE, retryAt: null, reason: 'offline'});
        return;
      }
      sessionRequestGenerationRef.current += 1;
      const requestGeneration = sessionRequestGenerationRef.current;
      const sessionGeneration = sessionGenerationRef.current;
      sessionAbortRef.current?.abort();
      if (sessionTimeoutRef.current) window.clearTimeout(sessionTimeoutRef.current);
      const controller = new AbortController();
      sessionAbortRef.current = controller;
      let timedOut = false;
      const timeoutId = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, D_SESSION_BOOTSTRAP_TIMEOUT_MS);
      sessionTimeoutRef.current = timeoutId;
      updatePlayback({
        status: userPausedRef.current ? PLAYER_STATES.PAUSED : PLAYER_STATES.CONNECTING,
        retryAt: null,
        reason: null
      });

      resolveDPlaySession(url, viewId, {signal: controller.signal})
        .then((session) => {
          if (
            controller.signal.aborted ||
            requestGeneration !== sessionRequestGenerationRef.current ||
            sessionGeneration !== sessionGenerationRef.current
          ) {
            return;
          }
          if (sessionAbortRef.current === controller) sessionAbortRef.current = null;
          window.clearTimeout(timeoutId);
          if (sessionTimeoutRef.current === timeoutId) sessionTimeoutRef.current = null;
          dSessionRef.current = session;
          setOwnServerViewId(session.viewId);
          if (relayStatusUrlRef.current !== session.statusUrl) {
            const initialSample = {
              state: session.state,
              upstreamReceiving: false,
              reserveSeconds: null,
              targetReserveSeconds: null,
              sampledAtMs: null,
              receivedAtMs: Date.now()
            };
            relaySampleRef.current = initialSample;
            setRelaySample(initialSample);
          }
          relayStatusUrlRef.current = session.statusUrl;
          setRelayStatusUrl(session.statusUrl);
          if (session.state === 'waiting') {
            // No media while over the screen limit: the manager UI replaces
            // the historical limit.ts slate entirely.
            destroyEngine();
            enterScreenManager(session);
            return;
          }
          createTransport(session.playlistUrl);
        })
        .catch((error) => {
          window.clearTimeout(timeoutId);
          if (sessionTimeoutRef.current === timeoutId) sessionTimeoutRef.current = null;
          if (sessionAbortRef.current === controller) sessionAbortRef.current = null;
          const isCurrentRequest =
            requestGeneration === sessionRequestGenerationRef.current &&
            sessionGeneration === sessionGenerationRef.current;
          if (!isCurrentRequest) return;
          if (controller.signal.aborted && !timedOut) return;
          // No legacy raw-media fallback: a failed D bootstrap always follows
          // the normal backoff, and only /play HTTP terminals end the session.
          const classification = timedOut
            ? {retryable: true, reason: 'network', statusCode: null}
            : classifyPlaybackFailure(error, 'network');
          if (userPausedRef.current) deferFailureWhilePaused(classification);
          else if (classification.retryable) scheduleRestartRef.current(classification.reason);
          else makeTerminal(classification);
        });
    },
    [
      createTransport,
      deferFailureWhilePaused,
      destroyEngine,
      enterScreenManager,
      makeTerminal,
      updatePlayback,
      viewId
    ]
  );
  createEngineRef.current = createEngine;

  const scheduleRestart = useCallback(
    (reason) => {
      if (restartTimerRef.current || playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR)
        return;
      if (userPausedRef.current) {
        deferFailureWhilePaused({retryable: true, reason});
        return;
      }
      const healthyMs = healthySinceRef.current ? Date.now() - healthySinceRef.current : 0;
      const next = nextUltraAttempt(attemptRef.current, healthyMs);
      const delayMs = computeUltraBackoffDelay(next);
      attemptRef.current = next;
      needsRecoveryConfirmationRef.current = true;
      healthySinceRef.current = null;
      readyToPlayRef.current = false;
      playRequestedRef.current = false;
      const lastFrag = lastFragRef.current;
      const video = videoRef.current;
      if (lastFrag && activePlaylistUrlRef.current && video && Number.isFinite(video.currentTime)) {
        resumeRef.current = {
          playlistUrl: activePlaylistUrlRef.current,
          sn: lastFrag.sn,
          offset: video.currentTime - lastFrag.start
        };
      } else {
        resumeRef.current = null;
      }
      destroyEngine();
      resetFreezeTracking();
      updatePlayback({attempt: next});
      playerHealth.report('player.retryScheduled', {variant: 'D', reason, attempt: next, delayMs});
      playerHealth.report('d.restartScheduled', {
        reason,
        attempt: next,
        delayMs,
        healthyMs,
        statusAgeMs: relaySampleRef.current
          ? Date.now() - Number(relaySampleRef.current.receivedAtMs || 0)
          : null
      });
      if (!onlineRef.current) {
        updatePlayback({status: PLAYER_STATES.OFFLINE, retryAt: null, reason: 'offline'});
        return;
      }
      const retryAt = Date.now() + delayMs;
      const sessionGeneration = sessionGenerationRef.current;
      updatePlayback({status: PLAYER_STATES.WAITING_RETRY, retryAt, reason});
      restartTimerRef.current = window.setTimeout(() => {
        restartTimerRef.current = null;
        if (sessionGeneration !== sessionGenerationRef.current) return;
        if (userPausedRef.current) {
          deferredFailureRef.current = {retryable: true, reason, statusCode: null};
          updatePlayback({status: PLAYER_STATES.PAUSED, retryAt: null, reason: null});
          return;
        }
        playerHealth.report('d.restarted', {reason, attempt: attemptRef.current});
        createEngineRef.current(playURL);
      }, delayMs);
    },
    [deferFailureWhilePaused, destroyEngine, playURL, resetFreezeTracking, updatePlayback]
  );
  scheduleRestartRef.current = scheduleRestart;

  const restartNow = useCallback(
    (reason = 'manual') => {
      const audioChange = reason === 'audioTrack' || reason === 'resume:audioTrack';
      if (reason !== 'manual' && !audioChange) {
        // Only one bootstrap/transport at a time. Online/offline flapping or
        // a resume that races a fresh transport must not chain full reloads.
        if (sessionAbortRef.current) {
          playerHealth.report('d.restartCoalesced', {reason, stage: 'bootstrap'});
          return;
        }
        if (hlsRef.current && Date.now() - createdAtRef.current < RESTART_COALESCE_MS) {
          playerHealth.report('d.restartCoalesced', {reason, stage: 'transport'});
          return;
        }
      }
      clearRestartTimer();
      if (initialTimerRef.current) window.clearTimeout(initialTimerRef.current);
      initialTimerRef.current = null;
      if (audioChange && lastFragRef.current && activePlaylistUrlRef.current) {
        resumeRef.current = {
          playlistUrl: activePlaylistUrlRef.current,
          sn: lastFragRef.current.sn,
          offset: (videoRef.current?.currentTime || 0) - lastFragRef.current.start
        };
      }
      if (audioChange) destroyEngine();
      playerHealth.report('player.retryNow', {variant: 'D', reason, attempt: attemptRef.current});
      if (!onlineRef.current) {
        updatePlayback({status: PLAYER_STATES.OFFLINE, retryAt: null, reason: 'offline'});
        return;
      }
      createEngineRef.current(playURL);
    },
    [clearRestartTimer, destroyEngine, playURL, updatePlayback]
  );
  restartNowRef.current = restartNow;

  const startFreedTransport = useCallback(() => {
    const session = dSessionRef.current;
    if (!session || playbackRef.current.status !== PLAYER_STATES.WAITING_SLOT) return;
    setSlotStarting(true);
    playerHealth.report('d.screenSlotFreed', {
      waitedMs: waitingSinceRef.current ? Date.now() - waitingSinceRef.current : null
    });
    createTransport(session.playlistUrl);
  }, [createTransport]);
  startFreedTransportRef.current = startFreedTransport;

  const refreshScreens = useCallback(async () => {
    if (playbackRef.current.status !== PLAYER_STATES.WAITING_SLOT) return;
    const sessionGeneration = sessionGenerationRef.current;
    setScreenState((current) => ({...current, loading: true}));
    try {
      const data = await getScreens(viewId);
      if (
        sessionGeneration !== sessionGenerationRef.current ||
        playbackRef.current.status !== PLAYER_STATES.WAITING_SLOT
      )
        return;
      const screens = Array.isArray(data?.screens) ? data.screens : [];
      setScreenState((current) => ({
        screens,
        maxActiveStreams: Number.isFinite(Number(data?.maxActiveStreams))
          ? Number(data.maxActiveStreams)
          : current.maxActiveStreams,
        activeCount: Number.isFinite(Number(data?.activeCount))
          ? Number(data.activeCount)
          : current.activeCount,
        loading: false,
        error: false,
        kickingKeys: current.kickingKeys.filter((key) =>
          screens.some((screen) => screen.key === key)
        )
      }));
      playerHealth.report('d.screensLoaded', {
        count: screens.length,
        max: Number.isFinite(Number(data?.maxActiveStreams)) ? Number(data.maxActiveStreams) : null
      });
    } catch (error) {
      if (
        sessionGeneration !== sessionGenerationRef.current ||
        playbackRef.current.status !== PLAYER_STATES.WAITING_SLOT
      )
        return;
      setScreenState((current) => ({...current, loading: false, error: true}));
      playerHealth.report('d.screenListError', {
        statusCode: Number(error?.statusCode) || null
      });
    }
  }, [viewId]);
  refreshScreensRef.current = refreshScreens;

  const handleKick = useCallback(
    (screen) => {
      if (!screen?.key) return;
      setScreenState((current) =>
        current.kickingKeys.includes(screen.key)
          ? current
          : {...current, kickingKeys: [...current.kickingKeys, screen.key]}
      );
      playerHealth.report('d.screenKickRequested', {targetChannel: screen.channelName || null});
      Promise.resolve(stopPlayer(screen.key))
        .then(() => {
          refreshScreensRef.current();
        })
        .catch((error) => {
          setScreenState((current) => ({
            ...current,
            kickingKeys: current.kickingKeys.filter((key) => key !== screen.key)
          }));
          playerHealth.report('d.screenKickFailed', {
            statusCode: Number(error?.statusCode) || null
          });
        });
    },
    []
  );

  const screenManagerActive = playback.status === PLAYER_STATES.WAITING_SLOT;

  useEffect(() => {
    if (!screenManagerActive) return undefined;
    refreshScreensRef.current();
    const timer = window.setInterval(() => refreshScreensRef.current(), SCREENS_POLL_MS);
    return () => window.clearInterval(timer);
  }, [screenManagerActive]);

  useEffect(() => {
    if (!screenManagerActive || !socket?.on) return undefined;
    const onSlotAvailable = () => {
      // Advisory and uncorrelated: it only accelerates the next checks. The
      // relay's own slot acquisition (observed through status.json) starts
      // the media, never this signal.
      playerHealth.report('d.slotSignal', {});
      refreshScreensRef.current();
    };
    socket.on('slotAvailable', onSlotAvailable);
    return () => socket.off?.('slotAvailable', onSlotAvailable);
  }, [screenManagerActive, socket]);

  useEffect(() => {
    if (!relayStatusUrl || typeof fetch !== 'function') return undefined;
    const playerSessionGeneration = sessionGenerationRef.current;
    let cancelled = false;
    let hidden = document.visibilityState === 'hidden';
    let failures = 0;
    let timer = null;
    let controller = null;
    let requestGeneration = 0;
    let poll = async () => {};
    const obsolete = () =>
      cancelled ||
      playerSessionGeneration !== sessionGenerationRef.current ||
      relayStatusUrlRef.current !== relayStatusUrl;

    const clearTimer = () => {
      if (timer) window.clearTimeout(timer);
      timer = null;
    };
    const schedule = (delayMs) => {
      clearTimer();
      if (!obsolete() && !hidden) timer = window.setTimeout(poll, delayMs);
    };
    poll = async () => {
      if (obsolete() || hidden) return;
      const generation = requestGeneration + 1;
      requestGeneration = generation;
      const requestController = new AbortController();
      controller = requestController;
      let timedOut = false;
      const timeout = window.setTimeout(() => {
        timedOut = true;
        requestController.abort();
      }, STATUS_FETCH_TIMEOUT_MS);
      try {
        const response = await fetch(relayStatusUrl, {
          method: 'GET',
          headers: {Accept: 'application/json'},
          credentials: 'same-origin',
          cache: 'no-store',
          signal: requestController.signal
        });
        if (!response.ok) throw new Error(`status:${response.status}`);
        const sample = validateRelayStatusPayload(await response.json(), Date.now());
        if (!sample) throw new Error('invalid-status-payload');
        if (obsolete() || hidden || generation !== requestGeneration) return;
        failures = 0;
        relaySampleRef.current = sample;
        setRelaySample(sample);
        if (
          playbackRef.current.status === PLAYER_STATES.WAITING_SLOT &&
          sample.state !== 'waiting' &&
          sample.state !== 'stopped'
        ) {
          // The relay acquired the freed slot on its own (waiting -> starting).
          startFreedTransportRef.current();
        }
        schedule(STATUS_POLL_INTERVAL_MS);
      } catch (error) {
        if (obsolete() || hidden || generation !== requestGeneration) return;
        if (error?.name === 'AbortError' && !timedOut) return;
        failures += 1;
        schedule(relayStatusBackoffMs(failures));
      } finally {
        window.clearTimeout(timeout);
        if (controller === requestController) controller = null;
      }
    };
    const onVisibilityChange = () => {
      if (obsolete()) return;
      hidden = document.visibilityState === 'hidden';
      clearTimer();
      if (hidden) {
        requestGeneration += 1;
        controller?.abort();
      } else {
        failures = 0;
        poll();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    if (!hidden) poll();
    return () => {
      cancelled = true;
      requestGeneration += 1;
      clearTimer();
      controller?.abort();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [relayStatusUrl]);

  useEffect(() => {
    const sampleStats = () => {
      const now = Date.now();
      const visible = document.visibilityState !== 'hidden';
      const video = videoRef.current;
      const hasTransport = Boolean(transportRef.current);
      const localBufferSeconds =
        visible && hasTransport
          ? strictBufferedSecondsAhead(video?.buffered, video?.currentTime)
          : null;
      const relay = relaySampleRef.current;
      const relayFresh = visible && relaySampleIsFresh(relay, now);
      const nextHistory = appendUltraHistory(
        historyRef.current,
        {
          reserveSeconds: relayFresh ? relay?.reserveSeconds : null,
          localBufferSeconds,
          targetReserveSeconds: Number.isFinite(relay?.targetReserveSeconds)
            ? relay.targetReserveSeconds
            : null
        },
        now
      );
      historyRef.current = nextHistory;
      setHistory(nextHistory);
      setStatsNow(now);

      const hlsMetrics = readHlsDiagnostics(hlsRef.current, video, bandwidthMeasuredRef.current);
      setDiagnostics({
        ...hlsMetrics,
        width: hlsMetrics.width ?? finiteMetric(video?.videoWidth),
        height: hlsMetrics.height ?? finiteMetric(video?.videoHeight),
        localBufferSeconds,
        reserveTrend: reserveTrendOverFiveSeconds(nextHistory, now),
        relayState: relayFresh ? relay?.state || null : null,
        playerState: playbackRef.current.status,
        transport: transportRef.current,
        droppedFrames: droppedVideoFrames(video),
        online: onlineRef.current,
        retryAttempt: attemptRef.current
      });
    };

    sampleStats();
    statsTimerRef.current = window.setInterval(sampleStats, STATS_SAMPLE_INTERVAL_MS);
    return () => {
      if (statsTimerRef.current) window.clearInterval(statsTimerRef.current);
      statsTimerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const onPlaying = () => {
      if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
      if (userPausedRef.current) {
        video.pause();
        updatePlayback({status: PLAYER_STATES.PAUSED, reason: null});
        return;
      }
      if (!onlineRef.current) {
        updatePlayback({status: PLAYER_STATES.OFFLINE, reason: 'offline'});
        return;
      }
      autoplayBlockedRef.current = false;
      healthySinceRef.current = Date.now();
      lastProgressAtRef.current = Date.now();
      if (
        needsRecoveryConfirmationRef.current ||
        playbackRef.current.status === PLAYER_STATES.BUFFERING ||
        playbackRef.current.status === PLAYER_STATES.OFFLINE
      ) {
        const available = bufferedSecondsAhead(video.buffered, video.currentTime);
        if (available >= RECOVERY_BUFFER_SECONDS && onlineRef.current) showRecovered();
        else updatePlayback({status: PLAYER_STATES.BUFFERING, reason: 'playbackStall'});
      } else if (playbackRef.current.status !== PLAYER_STATES.PLAYING) {
        updatePlayback({status: PLAYER_STATES.PLAYING, retryAt: null, reason: null});
      }
    };
    const onWaiting = () => {
      if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
      if (userPausedRef.current || !onlineRef.current) return;
      const aliveness = relayAliveness(relaySampleRef.current, Date.now());
      if (aliveness.status === 'alive') {
        // A dry buffer with a live relay session is the freeze itself: keep
        // the frame, let the watchdog decide the chip. No BUFFERING overlay.
        return;
      }
      updatePlayback({status: PLAYER_STATES.BUFFERING, reason: 'playbackStall'});
    };
    const onEnded = () => {
      if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
      if (userPausedRef.current) return;
      // A live session has no end. Treat it as a buffer underrun and let the
      // watchdog decide instead of replacing the session.
      playerHealth.report('d.endedIgnored', {});
      if (relayAliveness(relaySampleRef.current, Date.now()).status !== 'alive') {
        updatePlayback({status: PLAYER_STATES.BUFFERING, reason: 'playbackStall'});
      }
    };
    const onError = () => {
      if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
      if (!hlsRef.current && !userPausedRef.current) scheduleRestartRef.current('network');
    };
    video.addEventListener('playing', onPlaying);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('stalled', onWaiting);
    video.addEventListener('ended', onEnded);
    video.addEventListener('error', onError);
    return () => {
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('stalled', onWaiting);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('error', onError);
    };
  }, [showRecovered, updatePlayback]);

  useEffect(() => {
    monitorTimerRef.current = window.setInterval(() => {
      const video = videoRef.current;
      if (!video || restartTimerRef.current) return;
      const now = Date.now();
      const aliveness = relayAliveness(relaySampleRef.current, now);

      if (aliveness.status !== alivenessStatusRef.current) {
        const previous = alivenessStatusRef.current;
        alivenessStatusRef.current = aliveness.status;
        if (aliveness.status === 'lost') {
          playerHealth.report('d.statusLost', {ageMs: Number.isFinite(aliveness.ageMs) ? aliveness.ageMs : null});
        } else if (previous === 'lost') {
          playerHealth.report('d.statusRecovered', {ageMs: aliveness.ageMs});
        }
      }

      // Over-limit hold: no media is attached, the manager UI owns the view.
      // The same liveness rules apply so a dead relay session re-bootstraps.
      if (playbackRef.current.status === PLAYER_STATES.WAITING_SLOT) {
        if (document.visibilityState === 'hidden') return;
        const decision = dStallDecision({aliveness, frozenForMs: 0});
        if (decision.action === 'restart') scheduleRestartRef.current(decision.reason);
        return;
      }

      if (!hlsRef.current && !video.src) return;
      const available = bufferedSecondsAhead(video.buffered, video.currentTime);
      setBufferSeconds((current) =>
        current === null || Math.abs(current - available) >= 0.2 ? available : current
      );

      if (document.visibilityState === 'hidden') {
        lastProgressAtRef.current = now;
        return;
      }
      if (!readyToPlayRef.current) {
        const decision = dStartupDecision(available, now - createdAtRef.current, aliveness);
        if (decision === 'ready' || decision === 'degraded') {
          readyToPlayRef.current = true;
          if (userPausedRef.current) updatePlayback({status: PLAYER_STATES.PAUSED, reason: null});
          else beginPlaybackRef.current(decision, available);
        } else if (decision === 'restart') {
          scheduleRestartRef.current('emptyStartupBuffer');
        }
        return;
      }
      if (userPausedRef.current || autoplayBlockedRef.current) {
        lastProgressAtRef.current = now;
        return;
      }
      const {progressed, reset} = evaluatePlayheadProgress(
        lastCurrentTimeRef.current,
        video.currentTime
      );
      if (progressed) {
        if (reset) {
          playerHealth.report('d.timelineReset', {
            from: Number(lastCurrentTimeRef.current.toFixed(2)),
            to: Number(video.currentTime.toFixed(2))
          });
        }
        lastCurrentTimeRef.current = video.currentTime;
        lastProgressAtRef.current = now;
        if (frozenReportedRef.current) {
          // Seamless resume: back to PLAYING directly, no RECOVERED flash.
          playerHealth.report('d.resumed', {
            frozenMs: frozenSinceRef.current ? now - frozenSinceRef.current : null,
            discontinuity: lastCcRef.current
          });
          resetFreezeTracking();
          if (playbackRef.current.status === PLAYER_STATES.FROZEN) {
            updatePlayback({status: PLAYER_STATES.PLAYING, retryAt: null, reason: null});
          }
        }
        if (
          playbackRef.current.status === PLAYER_STATES.BUFFERING &&
          available >= RECOVERY_BUFFER_SECONDS &&
          onlineRef.current
        ) {
          showRecovered();
        }
      } else if (playRequestedRef.current) {
        const noProgressMs = now - lastProgressAtRef.current;
        if (
          noProgressMs >= FREEZE_DETECT_MS &&
          aliveness.status === 'alive' &&
          !frozenReportedRef.current
        ) {
          frozenReportedRef.current = true;
          frozenSinceRef.current = lastProgressAtRef.current;
          playerHealth.report('d.frozen', {
            bufferSeconds: Number(available.toFixed(2)),
            relayState: aliveness.state,
            statusAgeMs: Number.isFinite(aliveness.ageMs) ? aliveness.ageMs : null
          });
        }
        const decision = dStallDecision({aliveness, frozenForMs: noProgressMs});
        if (decision.action === 'restart') {
          scheduleRestartRef.current(decision.reason);
          return;
        }
        if (
          decision.showChip &&
          playbackRef.current.status !== PLAYER_STATES.FROZEN &&
          (playbackRef.current.status === PLAYER_STATES.PLAYING ||
            playbackRef.current.status === PLAYER_STATES.BUFFERING ||
            playbackRef.current.status === PLAYER_STATES.RECOVERED)
        ) {
          updatePlayback({status: PLAYER_STATES.FROZEN, retryAt: null, reason: decision.reason});
          if (!frozenChipReportedRef.current) {
            frozenChipReportedRef.current = true;
            playerHealth.report('d.frozenChipShown', {
              frozenMs: frozenSinceRef.current ? now - frozenSinceRef.current : null
            });
          }
        }
        if (frozenReportedRef.current) {
          // Splice-gap safety net: seek once into media that resumed wholly
          // ahead of the stuck playhead, beyond hls.js's own gap nudging.
          const target = resumeSeekTarget(video.buffered, video.currentTime);
          if (target !== null) {
            playerHealth.report('d.resumeNudge', {
              from: Number(video.currentTime.toFixed(2)),
              to: Number(target.toFixed(2)),
              gapSeconds: Number((target - video.currentTime).toFixed(2))
            });
            try {
              video.currentTime = target;
            } catch (_) {
              // The next watchdog tick re-evaluates.
            }
          }
        }
      }
      if (
        lastMediaRecoveryAtRef.current &&
        now - lastMediaRecoveryAtRef.current >= HEALTHY_RESET_MS
      ) {
        mediaRecoveryAttemptsRef.current = 0;
        inplaceReloadAttemptsRef.current = 0;
        lastMediaRecoveryAtRef.current = 0;
      }
      if (
        playbackRef.current.status === PLAYER_STATES.PLAYING &&
        available < 0.75 &&
        video.readyState < 3 &&
        aliveness.status !== 'alive'
      ) {
        updatePlayback({status: PLAYER_STATES.BUFFERING, reason: 'playbackStall'});
      }
    }, MONITOR_INTERVAL_MS);
    return () => {
      if (monitorTimerRef.current) window.clearInterval(monitorTimerRef.current);
      monitorTimerRef.current = null;
    };
  }, [resetFreezeTracking, showRecovered, updatePlayback]);

  useEffect(() => {
    const handleOffline = () => {
      onlineRef.current = false;
      setOnline(false);
      if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
      clearRestartTimer();
      if (recoveredTimerRef.current) window.clearTimeout(recoveredTimerRef.current);
      updatePlayback({status: PLAYER_STATES.OFFLINE, retryAt: null, reason: 'offline'});
      playerHealth.report('player.offline', {variant: 'D'});
    };
    const handleOnline = () => {
      onlineRef.current = true;
      setOnline(true);
      playerHealth.report('player.online', {variant: 'D'});
      if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
      const video = videoRef.current;
      const available = bufferedSecondsAhead(video?.buffered, video?.currentTime);
      if (userPausedRef.current) {
        updatePlayback({status: PLAYER_STATES.PAUSED, reason: null});
      } else if (hlsRef.current && available >= RECOVERY_BUFFER_SECONDS) {
        showRecovered();
      } else {
        restartNowRef.current('online');
      }
    };
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [clearRestartTimer, showRecovered, updatePlayback]);

  useEffect(() => {
    sessionGenerationRef.current += 1;
    const sessionGeneration = sessionGenerationRef.current;
    sessionRequestGenerationRef.current += 1;
    sessionAbortRef.current?.abort();
    sessionAbortRef.current = null;
    if (sessionTimeoutRef.current) window.clearTimeout(sessionTimeoutRef.current);
    sessionTimeoutRef.current = null;
    attemptRef.current = 0;
    healthySinceRef.current = null;
    deferredFailureRef.current = null;
    needsRecoveryConfirmationRef.current = false;
    dSessionRef.current = null;
    waitingSinceRef.current = 0;
    alivenessStatusRef.current = 'alive';
    resetFreezeTracking();
    setBufferSeconds(null);
    setScreenState(EMPTY_SCREENS);
    setOwnServerViewId(null);
    setSlotStarting(false);
    relayStatusUrlRef.current = null;
    relaySampleRef.current = null;
    historyRef.current = [];
    setRelayStatusUrl(null);
    setRelaySample(null);
    setHistory([]);
    setStatsNow(Date.now());
    clearRestartTimer();
    destroyEngine();
    updatePlayback({status: PLAYER_STATES.PREPARING, attempt: 0, retryAt: null, reason: null});
    initialTimerRef.current = window.setTimeout(() => {
      initialTimerRef.current = null;
      if (sessionGeneration !== sessionGenerationRef.current) return;
      if (onlineRef.current) createEngineRef.current(playURL);
      else updatePlayback({status: PLAYER_STATES.OFFLINE, reason: 'offline'});
    }, INITIAL_CONNECT_DELAY_MS);
    return () => {
      sessionGenerationRef.current += 1;
      sessionRequestGenerationRef.current += 1;
      sessionAbortRef.current?.abort();
      sessionAbortRef.current = null;
      if (sessionTimeoutRef.current) window.clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
      if (initialTimerRef.current) window.clearTimeout(initialTimerRef.current);
      initialTimerRef.current = null;
      clearRestartTimer();
      if (recoveredTimerRef.current) window.clearTimeout(recoveredTimerRef.current);
      recoveredTimerRef.current = null;
      destroyEngine();
    };
  }, [clearRestartTimer, destroyEngine, playURL, resetFreezeTracking, updatePlayback]);

  const requestPause = useCallback(() => {
    if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
    if (playbackRef.current.status === PLAYER_STATES.WAITING_SLOT) return;
    userPausedRef.current = true;
    autoplayBlockedRef.current = false;
    if (restartTimerRef.current) {
      deferredFailureRef.current = {
        retryable: true,
        reason: playbackRef.current.reason || 'network',
        statusCode: null
      };
      clearRestartTimer();
    }
    onUserPausedChange(true);
    videoRef.current?.pause();
    updatePlayback({status: PLAYER_STATES.PAUSED, reason: null});
    playerHealth.report('player.userPause', {variant: 'D'});
    playerHealth.report('d.userPause', {});
  }, [clearRestartTimer, onUserPausedChange, updatePlayback]);

  const requestPlay = useCallback(() => {
    if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
    if (playbackRef.current.status === PLAYER_STATES.WAITING_SLOT) return;
    userPausedRef.current = false;
    autoplayBlockedRef.current = false;
    onUserPausedChange(false);
    playerHealth.report('player.userPlay', {variant: 'D'});
    playerHealth.report('d.userPlay', {});
    const deferredFailure = deferredFailureRef.current;
    deferredFailureRef.current = null;
    if (deferredFailure) {
      if (!deferredFailure.retryable) {
        makeTerminal(deferredFailure);
      } else {
        restartNowRef.current(`resume:${deferredFailure.reason}`);
      }
      return;
    }
    if (!hlsRef.current && !videoRef.current?.src) {
      restartNowRef.current('userPlay');
      return;
    }
    if (readyToPlayRef.current) {
      const available = bufferedSecondsAhead(
        videoRef.current?.buffered,
        videoRef.current?.currentTime
      );
      beginPlaybackRef.current('user', available);
    } else {
      updatePlayback({status: PLAYER_STATES.STABILIZING, reason: null});
    }
  }, [makeTerminal, onUserPausedChange, updatePlayback]);

  const selectAudioTrack = (id) => {
    if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
    if (deferredFailureRef.current && !deferredFailureRef.current.retryable) return;
    if (audio.select(id)) return;
    if (userPausedRef.current) {
      deferredFailureRef.current = {retryable: true, reason: 'audioTrack', statusCode: null};
      return;
    }
    restartNowRef.current('audioTrack');
  };

  return (
    <>
      <PlayerChrome
        containerRef={containerRef}
        videoRef={videoRef}
        audioTracks={audioTracks}
        selectedAudioTrack={selectedAudioTrack}
        onAudioTrackChange={selectAudioTrack}
        status={playback.status}
        variant="D"
        playingName={playingName}
        attempt={playback.attempt}
        retryAt={playback.retryAt}
        reason={playback.reason}
        bufferSeconds={bufferSeconds}
        userPaused={userPaused}
        online={online}
        ultraReserveLabel={reserveChipLabel}
        ultraReserveStatus={reserveView.label}
        ultraReserveColor={reserveView.color}
        onRequestPlay={requestPlay}
        onRequestPause={requestPause}
        onRetryNow={() => restartNow('manual')}
      >
        <video
          ref={videoRef}
          playsInline
          preload="auto"
          style={{width: '100%', height: '100%', display: 'block', objectFit: 'contain'}}
        >
          Tu navegador no permite reproducir vídeo.
        </video>
        {screenManagerActive && (
          <ScreenManagerPanel
            screens={screenState.screens}
            maxActiveStreams={screenState.maxActiveStreams}
            activeCount={screenState.activeCount}
            ownViewId={ownServerViewId}
            loading={screenState.loading}
            error={screenState.error}
            kickingKeys={screenState.kickingKeys}
            starting={slotStarting}
            onKick={handleKick}
            onRetryLoad={() => refreshScreensRef.current()}
          />
        )}
      </PlayerChrome>
      <div id="d-advanced-panel">
        <Collapse in={advancedStatsEnabled} timeout="auto" unmountOnExit>
          <UltraPlayerStatsPanel
            history={history}
            now={statsNow}
            presentation={reserveView}
            diagnostics={diagnostics}
          />
        </Collapse>
      </div>
    </>
  );
}

DPlayer.propTypes = {
  playURL: PropTypes.string.isRequired,
  playingName: PropTypes.string,
  viewId: PropTypes.string.isRequired,
  bindVideoElement: PropTypes.func,
  userPaused: PropTypes.bool.isRequired,
  onUserPausedChange: PropTypes.func.isRequired,
  onRefreshVariant: PropTypes.func,
  advancedStatsEnabled: PropTypes.bool,
  socket: PropTypes.object
};

export default DPlayer;
